import { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/lib/company";

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const isActive = (to) => location.pathname === to;

  return (
    <header
      data-testid="global-header"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0B1221]/95 border-b border-slate-800 shadow-[0_4px_30px_rgba(0,0,0,0.5)]"
    >
      {/* TOP BANNER ROW - Large prominent logo */}
      <div className="border-b border-slate-800/60 bg-gradient-to-r from-[#020617] via-[#0B1221] to-[#020617] relative overflow-hidden">
        <div className="absolute inset-0 aios-starfield opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-3 lg:py-4 flex items-center justify-between gap-6">
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-4 lg:gap-6 group">
            <img
              src={COMPANY.logoUrl}
              onError={(e) => { e.currentTarget.src = COMPANY.logoFallback; }}
              alt="All In One Sourcing Specialists A.I.O"
              className="h-16 w-16 sm:h-20 sm:w-20 lg:h-28 lg:w-28 object-contain drop-shadow-[0_0_25px_rgba(220,38,38,0.35)] transition-transform group-hover:scale-105 shrink-0"
              data-testid="header-logo-img"
            />
            <div className="flex flex-col leading-tight">
              <span className="font-display font-extrabold text-white text-base sm:text-xl lg:text-3xl tracking-tighter">
                ALL IN ONE SOURCING
              </span>
              <span className="font-display text-red-500 text-[10px] sm:text-xs lg:text-sm tracking-[0.3em] font-bold mt-1">
                SPECIALISTS A.I.O
              </span>
              <span className="hidden lg:block text-slate-400 text-xs tracking-wide mt-1 font-medium">
                Aggressive Global Sourcing &middot; One Vendor &middot; One Invoice
              </span>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-4">
            <a
              href={COMPANY.phoneLink}
              data-testid="header-call-cta"
              className="aios-btn-primary text-xs lg:text-sm px-5 py-3"
            >
              <Phone className="w-4 h-4" />
              {COMPANY.phone}
            </a>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* SECONDARY NAV ROW */}
      <div className="hidden lg:block">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <nav className="flex items-center justify-center gap-2" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={link.testId}
                className={`px-6 py-4 font-display text-sm font-semibold tracking-widest uppercase transition-colors border-b-2 ${
                  isActive(link.to)
                    ? "text-red-500 border-red-600"
                    : "text-slate-300 hover:text-white border-transparent hover:border-slate-600"
                }`}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      {/* MOBILE NAV DRAWER */}
      {open && (
        <div className="lg:hidden border-t border-slate-800 py-4 bg-[#0B1221]" data-testid="mobile-nav">
          <div className="flex flex-col gap-1 px-6">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`mobile-${link.testId}`}
                onClick={() => setOpen(false)}
                className={`px-4 py-3 font-display font-semibold tracking-wide uppercase text-sm ${
                  isActive(link.to) ? "text-red-500 bg-slate-900" : "text-slate-200"
                }`}
              >
                {link.label}
              </NavLink>
            ))}
            <a
              href={COMPANY.phoneLink}
              data-testid="mobile-call-cta"
              className="aios-btn-primary mt-3 text-xs"
            >
              <Phone className="w-4 h-4" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
